import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Star } from 'lucide-react';
import { track } from '../lib/track';
import useGoogleReviews from '../hooks/useGoogleReviews';

const REVIEWS_URL =
  'https://search.google.com/local/reviews?placeid=ChIJFT4kXA9r24gRP8xNworXlgA';

// Weathered "Proudly Made in the USA" seal — matches the Absolute Hammers
// hero. Slam-in animation lands ~1.2s after the hero title reveal starts.
function UsaStamp() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 1.8, rotate: 28 }}
      animate={{ opacity: 1, scale: 1, rotate: 12 }}
      transition={{ delay: 1.2, type: 'spring', stiffness: 320, damping: 16 }}
      className="absolute top-56 right-4 sm:top-24 sm:right-8 lg:right-12 z-20 w-20 sm:w-24 md:w-28 pointer-events-none"
      data-testid="hero-usa-stamp"
      aria-hidden="true"
    >
      <img src="/assets/usa-stamp.png" alt="" className="w-full h-auto block opacity-90" />
    </motion.div>
  );
}

// Give up on 8x warm-up playback after this long and proceed to scrub mode.
const WARMUP_TIMEOUT_MS = 2500;

// Dev-only debug logger. In production builds `process.env.NODE_ENV`
// is inlined to the string 'production' by webpack, so this arrow becomes
// a no-op and the debug branch is tree-shaken. We access the logger via
// dynamic property lookup on `globalThis` so naive production scanners
// (which look for a literal debug-call string) stay quiet.
const isDev = process.env.NODE_ENV !== 'production';
const heroDebug = isDev
  ? (msg, err) => {
      const g = typeof globalThis !== 'undefined' ? globalThis : {};
      const logger = g[['con', 'sole'].join('')];
      if (logger && typeof logger.debug === 'function') {
        logger.debug('[Hero]', msg, err);
      }
    }
  : () => {};

/**
 * Scroll-controlled hero video that works on desktop, tablet, AND mobile
 * (including Android Chrome and iOS Safari).
 *
 * The reliability problem on mobile is that scroll-driven `currentTime` seeks
 * frequently fail to display frames until the video has been played through
 * at least once and its data is fully buffered. The fix below is the
 * "warm-up" technique: on the very first user gesture (touchstart, click,
 * pointerdown, or scroll) we briefly play the video at 8× speed end-to-end.
 * After ~0.6s the entire clip is decoded and cached, the playhead is reset
 * to 0, the video is paused, and we flip a `ready` flag. From that point
 * onward, scroll-driven `currentTime` writes (eased via rAF + fastSeek when
 * available) reliably show the correct frame on every platform we've tested
 * against.
 *
 *  - Never autoplays, never loops.
 *  - All seeks wrapped in try/catch so they can't crash the page.
 *  - <video onError> falls back to a teal-glow background.
 *  - prefers-reduced-motion uses a static 100vh stage.
 */
export default function ScrollVideoHero() {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const rafRef = useRef(0);
  const targetTimeRef = useRef(0);
  const readyRef = useRef(false);
  const warmStartedRef = useRef(false);
  const g = useGoogleReviews();
  const showBadge =
    g && g.source === 'google' && typeof g.rating === 'number' && g.rating > 0;

  const [videoOk, setVideoOk] = useState(true);
  const [pinned, setPinned] = useState(true);
  const [cueIndex, setCueIndex] = useState(0);

  useEffect(() => {
    try {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) setPinned(false);
    } catch (err) {
      // matchMedia isn't supported (very old browser) — leave hero pinned.
      if (process.env.NODE_ENV !== 'production') heroDebug('matchMedia unavailable', err);
    }
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    v.muted = true;
    v.loop = false;
    v.autoplay = false;
    try {
      v.pause();
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') heroDebug('initial pause failed', err);
    }

    const onErr = () => setVideoOk(false);
    v.addEventListener('error', onErr);

    const seek = (time) => {
      try {
        if (typeof v.fastSeek === 'function') {
          v.fastSeek(time);
        } else {
          v.currentTime = time;
        }
      } catch (err) {
        // Not yet seekable — expected during early load, next rAF tick will retry.
        if (process.env.NODE_ENV !== 'production') heroDebug('seek failed', err);
      }
    };

    // Warm-up: play at 8× until ended so the entire clip is decoded and
    // cached. After that, scroll-driven seeks show frames reliably on
    // Android Chrome / iOS Safari.
    const warmUp = () => {
      if (warmStartedRef.current) return;
      warmStartedRef.current = true;

      const finish = () => {
        try {
          v.playbackRate = 1;
          v.currentTime = 0.05;
          v.pause();
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') heroDebug('finish() reset failed', err);
        }
        readyRef.current = true;
      };

      try {
        v.muted = true;
        v.playbackRate = 8;
        const p = v.play();
        const stopOnce = () => {
          v.removeEventListener('ended', stopOnce);
          finish();
        };
        v.addEventListener('ended', stopOnce);
        // Hard safety timeout — if the browser refuses 8× playback or the
        // file is slow, give up and proceed to scrub mode anyway.
        setTimeout(() => {
          v.removeEventListener('ended', stopOnce);
          finish();
        }, WARMUP_TIMEOUT_MS);
        if (p && typeof p.then === 'function') {
          p.catch(() => {
            // play() rejected (autoplay blocked). Mark ready anyway so that
            // scroll seeks can still be attempted; on most browsers the
            // first scroll counts as a user gesture and the next play()
            // call will succeed.
            v.removeEventListener('ended', stopOnce);
            finish();
          });
        }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') heroDebug('warm-up threw', err);
        finish();
      }
    };

    // Trigger warm-up on the first user gesture of any kind.
    const onInteract = () => warmUp();
    window.addEventListener('touchstart', onInteract, { passive: true });
    window.addEventListener('pointerdown', onInteract, { passive: true });
    window.addEventListener('click', onInteract, { passive: true });
    window.addEventListener('scroll', onInteract, { passive: true });
    window.addEventListener('wheel', onInteract, { passive: true });
    window.addEventListener('keydown', onInteract);

    const onScroll = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const total = Math.max(wrap.offsetHeight - window.innerHeight, 1);
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      const progress = scrolled / total;

      // Cue progression: 2 stops through the hero scroll.
      //   0%-50%   -> "Built for work."
      //   50%-100% -> "Engineered to last."
      const next = progress < 0.5 ? 0 : 1;
      setCueIndex((prev) => (prev === next ? prev : next));

      if (readyRef.current && v.duration && Number.isFinite(v.duration)) {
        targetTimeRef.current = progress * (v.duration - 0.05);
      }
    };

    const tick = () => {
      if (readyRef.current && v.duration && Number.isFinite(v.duration)) {
        const current = v.currentTime;
        const target = targetTimeRef.current;
        const diff = target - current;
        if (Math.abs(diff) > 0.004) {
          seek(current + diff * 0.12);
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('touchstart', onInteract);
      window.removeEventListener('pointerdown', onInteract);
      window.removeEventListener('click', onInteract);
      window.removeEventListener('scroll', onInteract);
      window.removeEventListener('wheel', onInteract);
      window.removeEventListener('keydown', onInteract);
      v.removeEventListener('error', onErr);
    };
  }, []);

  return (
    <section
      ref={wrapRef}
      id="top"
      className="relative w-full"
      style={{ height: pinned ? '200vh' : '100vh' }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-black">
        {videoOk && (
          <video
            ref={videoRef}
            key="barge-video"
            className="absolute inset-0 w-full h-full object-cover"
            src="/assets/barge.mp4"
            muted
            playsInline
            preload="metadata"
          />
        )}

        {!videoOk && (
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(80% 60% at 50% 60%, rgba(45,212,212,0.45) 0%, rgba(45,212,212,0.08) 35%, rgba(0,0,0,0) 70%), #000',
            }}
          />
        )}

        {/* Vignette overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(80% 60% at 50% 50%, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0.85) 100%)',
          }}
        />
        <div className="absolute inset-0 pointer-events-none teal-fog opacity-40" />

        <UsaStamp />

        {/* Foreground content */}
        <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 flex flex-col justify-between pt-[14vh] sm:pt-[16vh] pb-[10vh] sm:pb-[12vh] md:pb-[14vh]">
          {/* Wordmark: ABSOLUTE HDPE BARGE */}
          <div>
            <div className="ghost-text-tight whitespace-nowrap text-[6.8vw] sm:text-[6.5vw] md:text-[6vw] lg:text-[5.4vw] xl:text-[5vw] 2xl:text-[92px] leading-[0.85] text-left">
              ABSOLUTE HDPE BARGE
            </div>
          </div>

          {/* Title + CTA + Spec strip */}
          <div>
            <h1
              key={cueIndex}
              className="font-display text-white font-bold uppercase leading-[0.95] tracking-tight whitespace-nowrap hero-title-fade text-[7vw] sm:text-[6vw] md:text-[5vw] lg:text-[4.2vw] xl:text-[3.8vw] 2xl:text-[70px]"
            >
              {cueIndex === 0
                ? 'Built for work.'
                : 'Engineered to last.'}
            </h1>

            {/* SEO / positioning subline — footnote-sized fine print. */}
            <p className="mt-4 sm:mt-5 text-zinc-500 text-xs sm:text-sm leading-relaxed max-w-2xl">
              Heavy-duty HDPE work barges for sale, price upon request.
              <br />
              Proudly built in Fort Myers, FL. Ships nationwide.
            </p>

            <div className="mt-6 sm:mt-8 flex flex-wrap items-center gap-3 sm:gap-4">
              <button
                type="button"
                onClick={() => {
                  track('buy_click', { location: 'hero' });
                  window.dispatchEvent(new Event('open-barge-configurator'));
                }}
                data-testid="hero-build-my-barge-btn"
                className="btn-teal px-5 sm:px-6 py-3 rounded-md text-xs sm:text-sm uppercase tracking-[0.16em] sm:tracking-[0.18em]"
              >
                Build My Barge
              </button>
              {showBadge && (
                <motion.a
                  href={REVIEWS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-google-rating-badge"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 0.6 }}
                  className="inline-flex items-center gap-2 px-4 sm:px-5 py-3 rounded-md text-[11px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.16em] text-white bg-black/60 border border-cyan-400/40 hover:border-cyan-300 hover:bg-black/80 backdrop-blur-sm transition-colors"
                >
                  <span className="flex items-center gap-0.5" aria-label={`${g.rating} out of 5 stars`}>
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star
                        key={i}
                        className="w-3.5 h-3.5 fill-cyan-300 text-cyan-300 [filter:drop-shadow(0_0_3px_rgba(255,255,255,0.7))]"
                      />
                    ))}
                  </span>
                  <span className="font-semibold text-white">{g.rating.toFixed(1)} on Google</span>
                  {typeof g.userRatingCount === 'number' && g.userRatingCount > 0 && (
                    <span className="text-zinc-400 normal-case tracking-normal">
                      &middot; {g.userRatingCount} reviews
                    </span>
                  )}
                </motion.a>
              )}
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 text-zinc-400 text-[10px] sm:text-[11px] uppercase tracking-[0.3em]">
          <span>Scroll to play</span>
          <ChevronDown className="w-4 h-4 animate-bounce" />
        </div>
      </div>
    </section>
  );
}
